import { memo } from 'react';
import { SLD_COLORS } from '../utils/sldTheme';
import type { SLDDeviceState } from '../utils/sldTypes';

interface MeterIndicatorProps {
  cx: number;          // center X (on the bus)
  cy: number;          // center Y
  label: string;
  activePowerKw: number | null;
  status: string;
  deviceState?: SLDDeviceState;
  onClick: () => void;
}

const RADIUS = 28;

function MeterIndicatorInner({ cx, cy, label, activePowerKw, status, deviceState, onClick }: MeterIndicatorProps) {
  // Resolve palette via deviceState first, fall back to legacy status string.
  const isFault = deviceState === 'fault';
  const isOffline = deviceState === 'offline';
  const isNoComms = deviceState === 'no_comms';
  const isUnknown = deviceState === 'unknown';
  const isDegraded = deviceState === 'degraded';
  const isLegacyOffline = !deviceState && (status ?? '').toLowerCase() === 'offline';
  const suppressNumerics = isFault || isOffline || isNoComms || isUnknown || isLegacyOffline;

  // Resolve color tokens for the meter circle.
  let strokeColor: string;
  let fillColor: string;
  let textColor: string;
  if (isFault) {
    strokeColor = '#fb7185';   // GAMMA.red — fault stroke
    fillColor = SLD_COLORS.fault.fill;
    textColor = '#fb7185';
  } else if (isOffline) {
    strokeColor = '#fb7185';
    fillColor = SLD_COLORS.offline.fill;
    textColor = '#fb7185';
  } else if (isNoComms) {
    // Meter has no separate dot — stroke serves as the amber NO COMMS signal.
    // Body fill from SLD_COLORS.stale; stroke stays GAMMA.amber for visibility.
    strokeColor = '#fbbf24';
    fillColor = SLD_COLORS.stale.fill;
    textColor = '#fbbf24';
  } else if (isUnknown) {
    strokeColor = '#475569';
    fillColor = SLD_COLORS.offline.fill;
    textColor = '#475569';
  } else if (isLegacyOffline) {
    strokeColor = SLD_COLORS.offline.stroke;
    fillColor = SLD_COLORS.offline.fill;
    textColor = SLD_COLORS.offline.text;
  } else if (isDegraded) {
    // 🪤 A degraded meter CANNOT be signalled with amber. SLD_COLORS.meter.stroke
    // is ALREADY GAMMA.amber (#fbbf24) — the healthy meter palette is amber, so
    // an "amber means impaired" arm here is byte-identical to the healthy branch
    // and renders nothing at all. Caught in self-review after the first attempt
    // did exactly that: a passing test suite would never have found it, because
    // the assertion "degraded is amber" is satisfied by the healthy palette.
    //
    // The carrier is therefore the stroke PATTERN, not its colour: same amber
    // body, dashed outline. The meter has no status dot and no corner badge slot
    // (unlike GeneratorCard/BESSCard), so dashing is the available axis.
    // Numerics are NOT suppressed — the meter is still reading.
    strokeColor = SLD_COLORS.meter.stroke;
    fillColor = SLD_COLORS.meter.fill;
    textColor = SLD_COLORS.meter.text;
  } else {
    strokeColor = SLD_COLORS.meter.stroke;
    fillColor = SLD_COLORS.meter.fill;
    textColor = SLD_COLORS.meter.text;
  }

  const power = activePowerKw != null ? Math.round(activePowerKw) : null;
  const powerStr = suppressNumerics
    ? '--'
    : power != null
      ? (Math.abs(power) >= 1000 ? `${(power / 1000).toFixed(1)} MW` : `${power} kW`)
      : '—';

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <circle
        cx={cx} cy={cy} r={RADIUS}
        fill={fillColor} stroke={strokeColor} strokeWidth={1.5}
        strokeDasharray={isDegraded ? '4 2' : undefined}
        data-degraded={isDegraded ? 'true' : undefined}
      />
      {isFault && (
        <circle cx={cx} cy={cy} r={RADIUS} fill="none" stroke={strokeColor} strokeWidth={1.5} className="sld-pulse" />
      )}
      <line
        x1={cx} y1={cy - RADIUS + 6} x2={cx} y2={cy + RADIUS - 6}
        stroke={strokeColor} strokeWidth={0.5} opacity={0.3}
      />
      <text
        x={cx} y={cy - 2} textAnchor="middle"
        fill={textColor} fontSize="14" fontWeight="700"
      >
        M
      </text>
      <text
        x={cx} y={cy + 12} textAnchor="middle"
        fill={textColor} fontSize="8" fontFamily="monospace"
      >
        {powerStr}
      </text>
      <text
        x={cx} y={cy + RADIUS + 14} textAnchor="middle"
        fill="#94a3b8" fontSize="10" fontFamily="monospace"
      >
        {label}
      </text>
    </g>
  );
}

export const MeterIndicator = memo(MeterIndicatorInner);
